import React, { useState, useMemo } from 'react';
import { Search, Filter, Plus, MapPin, Plane, SlidersHorizontal } from 'lucide-react';
import TripCard from './TripCard';
import { useFamilyContext } from '../../../contexts/FamilyContext';

export default function TripBrowseGrid({ 
  trips = [], 
  onTripSelect,
  onAddTrip,
  showFilters = false,
  onToggleFilters 
}) {
  const { currentUser, family } = useFamilyContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPhase, setFilterPhase] = useState('all');
  const [filterTransport, setFilterTransport] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  
  // Filter and sort trips
  const filteredTrips = useMemo(() => {
    let filtered = [...trips];
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(trip =>
        trip.title?.toLowerCase().includes(query) ||
        trip.location?.toLowerCase().includes(query) ||
        trip.description?.toLowerCase().includes(query) ||
        trip.trip_metadata?.trip_features?.some(f => f.toLowerCase().includes(query))
      );
    }
    
    // Phase filter
    if (filterPhase !== 'all') {
      filtered = filtered.filter(trip => 
        trip.trip_metadata?.trip_phase === filterPhase
      );
    }
    
    // Transport filter
    if (filterTransport !== 'all') {
      filtered = filtered.filter(trip => 
        trip.trip_metadata?.transport_type === filterTransport
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'budget-low':
          const aMin = a.trip_metadata?.budget_breakdown?.total?.min || Infinity;
          const bMin = b.trip_metadata?.budget_breakdown?.total?.min || Infinity;
          return aMin - bMin;
        case 'budget-high':
          const aMax = a.trip_metadata?.budget_breakdown?.total?.max || 0;
          const bMax = b.trip_metadata?.budget_breakdown?.total?.max || 0;
          return bMax - aMax;
        case 'rating':
          const aRating = Object.values(a.ratings || {}).reduce((s,r) => s+r, 0) / (Object.keys(a.ratings || {}).length || 1);
          const bRating = Object.values(b.ratings || {}).reduce((s,r) => s+r, 0) / (Object.keys(b.ratings || {}).length || 1);
          return bRating - aRating;
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [trips, searchQuery, filterPhase, filterTransport, sortBy]);
  
  // Get trip stats
  const tripStats = useMemo(() => {
    const stats = {
      total: trips.length,
      ideas: trips.filter(t => t.trip_metadata?.trip_phase === 'idea').length,
      researching: trips.filter(t => t.trip_metadata?.trip_phase === 'researching').length,
      planning: trips.filter(t => t.trip_metadata?.trip_phase === 'planning').length,
      booked: trips.filter(t => t.trip_metadata?.trip_phase === 'booked').length,
      completed: trips.filter(t => t.trip_metadata?.trip_phase === 'completed').length
    };
    return stats;
  }, [trips]);
  
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl border border-stone-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-stone-900">Family Trips</h2>
            <p className="text-sm text-stone-600 mt-1">
              Plan your next adventure together
            </p>
          </div>
          <button
            onClick={onAddTrip}
            className="px-4 py-2 bg-gold-500 text-white rounded-lg hover:bg-gold-600 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Plan Trip</span>
          </button>
        </div>
        
        {/* Trip Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-azure-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-azure-900">{tripStats.ideas}</div>
            <div className="text-xs text-azure-700">Ideas</div>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-amber-900">{tripStats.researching}</div>
            <div className="text-xs text-amber-700">Researching</div>
          </div>
          <div className="bg-violet-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-violet-900">{tripStats.planning}</div>
            <div className="text-xs text-violet-700">Planning</div>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-emerald-900">{tripStats.booked}</div>
            <div className="text-xs text-emerald-700">Booked</div>
          </div>
          <div className="bg-stone-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-stone-900">{tripStats.completed}</div>
            <div className="text-xs text-stone-700">Completed</div>
          </div>
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-white rounded-xl border border-stone-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search Bar */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              placeholder="Search trips by destination, title, or features..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
            />
          </div>
          
          {/* Filter Controls */}
          <div className="flex gap-2">
            <select
              value={filterPhase}
              onChange={(e) => setFilterPhase(e.target.value)}
              className="px-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
            >
              <option value="all">All Phases</option>
              <option value="idea">💡 Ideas</option>
              <option value="researching">🔍 Researching</option>
              <option value="planning">📋 Planning</option>
              <option value="booked">✅ Booked</option>
              <option value="completed">🎉 Completed</option>
            </select>
            
            <select
              value={filterTransport}
              onChange={(e) => setFilterTransport(e.target.value)}
              className="px-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
            >
              <option value="all">All Transport</option>
              <option value="drive">🚗 Drive</option>
              <option value="fly">✈️ Fly</option>
              <option value="cruise">🚢 Cruise</option>
              <option value="train">🚂 Train</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
            >
              <option value="newest">Newest First</option>
              <option value="rating">Best Rated</option>
              <option value="budget-low">Budget: Low to High</option>
              <option value="budget-high">Budget: High to Low</option>
            </select>
            
            {onToggleFilters && (
              <button
                onClick={onToggleFilters}
                className={`p-2 rounded-lg transition-colors ${
                  showFilters ? 'bg-gold-500 text-white' : 'border border-stone-200 text-stone-600 hover:bg-stone-50'
                }`}
              >
                <SlidersHorizontal className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Trip Grid */}
      {filteredTrips.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTrips.map((trip) => (
            <TripCard
              key={trip.id}
              trip={trip}
              onClick={(trip, originElement) => onTripSelect(trip, originElement)}
              currentUser={currentUser}
              family={family}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="text-6xl mb-4">✈️</div>
            <h3 className="text-xl font-semibold text-stone-900 mb-2">
              {searchQuery ? 'No trips found' : 'No trips planned yet'}
            </h3>
            <p className="text-stone-600 mb-6">
              {searchQuery 
                ? `No trips match "${searchQuery}". Try a different search.`
                : 'Start planning your family adventures by adding your first trip idea!'
              }
            </p>
            {!searchQuery && (
              <button
                onClick={onAddTrip}
                className="px-6 py-3 bg-gold-500 text-white rounded-lg hover:bg-gold-600 transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                <span>Plan Your First Trip</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}